import * as React from "react";
import { Form, Checkbox, useFormApi, useFormState } from "informedv4";
import { v4 } from "uuid";
import Modal, { ModalSize } from "../../misc/components/modal";
import { Dropdown } from "../../forms/informedv4/DropdownV4";
import { TextBox } from "../../forms/informedv4/TextBoxV4";
import { EDiarySessionConfiguration } from "./EDiarySessionConfiguration";
import { Required, RequiredNotBlank } from "../../forms/validation/Required";
import { ScheduleSession, } from "./VisitSchedule";
import { DeviceTypes } from "../../misc/api/apiTypes";
import { SessionTypes } from "../../misc/api/ApiSessionTypes";
import { ErrorBoundary } from "../../errors/ErrorBoundary";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { MultiSelect } from "../../forms/informedv4/MultiSelectV4";
import { Study } from "webportalclient";
import { useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { MaxLengthValidator } from "../../forms/validation/ConfigurableItemValidator";

export interface SessionConfigurationProps {
    study: Study;
    possibleRequiredSessions: any[];
    possiblePreSessions: any[];
    sessionItem: ScheduleSession;
    onComplete: (session: ScheduleSession) => void;
    onClose: () => void;
    onClone: () => void;
}

interface SessionConfigurationForm {
    Label: string;
    Rules: string;
    Description: string;
    Rolling: boolean;
    ContinueTest: boolean;
    Mandatory: boolean;
    Disabled: boolean;
    Group: string;
    SessionType: SessionTypes;
    DeviceType: DeviceTypes;
    RequiredSessions: any[];
    PreSessionItemId: string;
    Cohort: string;
    AfterRandomisation: boolean;
    VisualReview: boolean;
    IncludedInDailySummaryCompliance?: boolean;
    Configuration?: string;
    ExpectedMinimumQuestionnaires?: number;
    ExpectedMinimumTests?: number;
    RequireDoesTime?: boolean;
    Tags?: string;
    Unscheduled: boolean;
}

interface EDiarySessionConfigurationForm extends SessionConfigurationForm {
    ResetDiaryOnUpload: boolean;
}

export const SessionConfiguration = ({ study, possibleRequiredSessions, sessionItem, possiblePreSessions, onComplete, onClose, onClone }: SessionConfigurationProps) => {
    const initialFormValues: EDiarySessionConfigurationForm = {
        Label: sessionItem?.label,
        Description: sessionItem?.description,
        Rules: sessionItem?.rules,
        Rolling: sessionItem?.rolling ?? false,
        ContinueTest: sessionItem?.continueTest ?? false,
        Mandatory: sessionItem?.isMandatory ?? false,
        Disabled: sessionItem?.disabled ?? false,
        SessionType: sessionItem?.sessionType,
        DeviceType: sessionItem?.deviceType,
        RequiredSessions: sessionItem?.requiredSessions,
        ResetDiaryOnUpload: sessionItem?.eDiary?.resetDiaryOnUpload,
        PreSessionItemId: sessionItem?.preSessionItemId,
        Group: sessionItem?.group,
        Cohort: sessionItem?.cohort,
        AfterRandomisation: sessionItem?.afterRandomisation,
        VisualReview: sessionItem?.visualReview,
        RequireDoesTime: sessionItem?.requireDoseTime,
        Tags: sessionItem?.tags,
        Unscheduled: sessionItem?.unscheduled,
        IncludedInDailySummaryCompliance: sessionItem?.eDiary?.includedInDailySummaryCompliance,
        Configuration: sessionItem?.eDiary?.configuration,
        ExpectedMinimumQuestionnaires: sessionItem?.eDiary?.expectedMinimumQuestionnaires,
        ExpectedMinimumTests: sessionItem?.eDiary?.expectedMinimumTests
    }

    return (
        <Modal show={true} header="Session Configuration" size={ModalSize.Large} closeModalHandler={onClose} automationId="SessionConfigurationModal">
            <ErrorBoundary>
                <Form validateOn="submit" noValidate initialValues={initialFormValues as unknown as Record<string, unknown>}>
                    <SessionConfigurationCore
                        study={study}
                        possibleRequiredSessions={possibleRequiredSessions}
                        possiblePreSessions={possiblePreSessions}
                        id={sessionItem?.id ?? v4()}
                        isCreate={!sessionItem}
                        onComplete={onComplete}
                        onClone={onClone}
                    />
                </Form>
            </ErrorBoundary>
        </Modal>
    );
}

export interface SessionConfigurationCoreProps {
    id: string;
    study: Study;
    possibleRequiredSessions: any[];
    possiblePreSessions: any[];
    isCreate: boolean;
    onComplete: (session: ScheduleSession) => void;
    onClone: () => void;
}

export const SessionConfigurationCore = ({ id, study, possibleRequiredSessions, possiblePreSessions, isCreate, onComplete, onClone }: SessionConfigurationCoreProps) => {
    const [sessionTypes, setSessionTypes] = React.useState([]);
    const [deviceTypes, setDeviceTypes] = React.useState([]);

    const legacyClient = useWebPortalLegacyClient();

    React.useEffect(() => {
        const init = async () => {
            const sessionTypeResult = await legacyClient.getStudySessionTypes(study.id);
            setSessionTypes(sessionTypeResult);
            const deviceTypeResult = await legacyClient.getStudyDeviceTypes(study.id);
            setDeviceTypes(deviceTypeResult);
        }

        init();
    }, [study.id]);

    const formApi = useFormApi();
    const state = useFormState();

    const onSubmit = async () => {
        formApi.setError(undefined,undefined);

        const error = formApi.getError("");
        formApi.validate();
        let state = formApi.getFormState();

        formApi.validate();
        state = formApi.getFormState();

        if (state.invalid)
            return;

        const session: ScheduleSession = {
            id: id,
            label: state.values.Label as string,
            isMandatory: state.values.Mandatory as boolean,
            rules: state.values.Rules as string,
            description: state.values.Description as string,
            disabled: state.values.Disabled as boolean,
            rolling: state.values.Rolling as boolean,
            continueTest: state.values.ContinueTest as boolean,
            deviceType: state.values.DeviceType as DeviceTypes,
            sessionType: state.values.SessionType as SessionTypes,
            requiredSessions: state.values.RequiredSessions as any[],
            preSessionItemId: state.values.PreSessionItemId as string,
            group: state.values.Group as string,
            cohort: state.values.Cohort as string,
            afterRandomisation: state.values.AfterRandomisation as boolean,
            visualReview: state.values.VisualReview as boolean,
            requireDoseTime: state.values.RequireDoesTime as boolean,
            tags: state.values.Tags as string,
            unscheduled: state.values.Unscheduled as boolean,
            eDiary: {
                resetDiaryOnUpload: state.values.ResetDiaryOnUpload as boolean,
                includedInDailySummaryCompliance: state.values.IncludedInDailySummaryCompliance as boolean,
                configuration: state.values.Configuration !== "blank" ? state.values.Configuration as string : null,
                expectedMinimumQuestionnaires: state.values.ExpectedMinimumQuestionnaires as number,
                expectedMinimumTests: state.values.ExpectedMinimumTests as number,
            }
        }

        onComplete(session);
    }
    const error = formApi.getError(undefined);

    return (
        <>
            <div className="modal-body">
                <Alert show={error as boolean} category={AlertCategoryEnum.Danger}>
                    {error}
                </Alert>
                <div className="row">
                    <div className="form-group col-md-6 col-xs-6">
                        <label htmlFor="Label">Label</label>
                        <TextBox name="Label" id="Label" field="Label" validate={v => RequiredNotBlank(v) ?? MaxLengthValidator(v as string, 40)} />
                    </div>
                    <div className="form-group col-md-6 col-xs-6">
                        <label htmlFor="Group">Group</label>
                        <TextBox name="Group" id="Group" field="Group"  />
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-6 col-sm-6 col-xs-6">
                        <label htmlFor="Mandatory">Mandatory</label>
                        <p className="help-block">Required to be performed in schedule</p>
                        <Checkbox name="Mandatory" id="Mandatory" field="Mandatory" />&nbsp;
                    </div>
                    <div className="form-group col-md-6 col-sm-6 col-xs-6">
                        <label htmlFor="Disabled">Disabled</label>
                        <p className="help-block">Disabled from use in the schedule</p>
                        <Checkbox name="Disabled" id="Disabled" field="Disabled" />&nbsp;
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-6 col-sm-6 col-xs-6">
                        <label htmlFor="Rolling">Rolling</label>
                        <p className="help-block">Can be continuously re-configured and performed even if it has already been used.</p>
                        <Checkbox name="Rolling" id="Rolling" field="Rolling" />&nbsp;
                    </div>
                    <div className="form-group col-md-6 col-sm-6 col-xs-6">
                        <label htmlFor="Unscheduled">Unscheduled</label>
                        <p className="help-block">Indicates the session is unscheduled</p>
                        <Checkbox name="Unscheduled" id="Unscheduled" field="Unscheduled" />&nbsp;
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-12 col-xs-12">
                        <label htmlFor="Description">Description</label>
                        <TextBox name="Description" id="Description" field="Description" />
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-12 col-xs-12">
                        <label htmlFor="Rules">Rules</label>
                        <TextBox name="Rules" id="Rules" field="Rules" />
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-6 col-xs-12">
                        <label htmlFor="SessionType">Session Type</label>
                        <Dropdown
                            name="SessionType"
                            id="SessionType"
                            field="SessionType"
                            validate={Required}
                            onChange={() => formApi.setValue("DeviceType", null)}
                        >
                            <option
                                key={"OptionBlank"}
                                value={[]}
                            ></option>
                            {sessionTypes.map(st => (
                                <option
                                    key={`Option${st.Id}`}
                                    value={st.Id}
                                >
                                    {st.Name}
                                </option>
                            ))}
                        </Dropdown>
                    </div>
                    <div className="form-group col-md-6 col-xs-12">
                        <label htmlFor="DeviceType">Device Type</label>
                        <Dropdown
                            name="DeviceType"
                            id="DeviceType"
                            field="DeviceType"
                            validate={Required}
                        >
                            <option
                                key={"OptionBlank"}
                                value={[]}
                            ></option>
                            {deviceTypes.filter(dt => dt.RelatedSessionTypes.includes(state.values.SessionType)).map(dt => (
                                <option
                                    key={`Option${dt.Id}`}
                                    value={dt.Id}
                                >
                                    {dt.Name}
                                </option>
                            ))}
                        </Dropdown>
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-12 col-xs-12">
                        <label htmlFor="RequiredSessions">Required Sessions</label>
                        <MultiSelect
                            name="RequiredSessions"
                            id="RequiredSessions"
                            field="RequiredSessions"
                            options={possibleRequiredSessions}
                            labelKey="Label"
                            valueKey="Id"
                            multi={true}
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="form-group col-md-12 col-xs-12">
                        <label htmlFor="preSessionItemId">Pre Session</label>
                        <Dropdown
                            name="PreSessionItemId"
                            id="preSessionItemId"
                            field="PreSessionItemId"
                        >
                            <option
                                key={"OptionBlank"}
                                value={[]}
                            ></option>
                            {possiblePreSessions.map(s => (
                                <option
                                    key={`Option${s.Id}`}
                                    value={s.Id}
                                >
                                    {s.Label}
                                </option>
                            ))}
                        </Dropdown>
                    </div>
                </div>
                {state.values.DeviceType === DeviceTypes.In2itiveP230EDiary ? <EDiarySessionConfiguration study={study} /> : null}
                <div className="row">
                    <div className="form-group col-md-6 col-xs-6">
                        <label htmlFor="Cohort">Cohort</label>
                        <p className="help-block">Additional metadata regarding the cohort the session is part of</p>
                        <TextBox name="Cohort" id="Cohort" field="Cohort" />
                    </div>
                    <div className="form-group col-md-6 col-xs-6">
                        <label htmlFor="Tags">Tags</label>
                        <p className="help-block">Additional metadata tags relating to the session</p>
                        <TextBox name="Tags" id="Tags" field="Tags" />
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-6 col-sm-6 col-xs-6">
                        <label htmlFor="AfterRandomisation">Available only after randomisation</label>
                        <p className="help-block">The session will only be available for selection after randomisation of the subject</p>
                        <Checkbox name="AfterRandomisation" id="AfterRandomisation" field="AfterRandomisation" />&nbsp;
                    </div>
                    {state.values.SessionType === SessionTypes.Cough ? <div className="form-group col-md-6 col-sm-6 col-xs-6">
                        <label htmlFor="VisualReview">Requires Review Step</label>
                        <p className="help-block">The session will require a Review step before Acceptance</p>
                        <Checkbox name="VisualReview" id="VisualReview" field="VisualReview" />&nbsp;
                    </div> : null}
                    {[SessionTypes.Fvc, SessionTypes.Svc].includes(state.values.SessionType as SessionTypes) && state.values.PreSessionItemId !== undefined ? <div className="form-group col-md-6 col-sm-6 col-xs-6">
                        <label htmlFor="RequireDoesTime">Requires Dose Time</label>
                        <p className="help-block">The session requires the entering of a dose time</p>
                        <Checkbox name="RequireDoesTime" id="RequireDoesTime" field="RequireDoesTime" />&nbsp;
                    </div> : null}
                    <div className="form-group col-md-6 col-sm-6 col-xs-6">
                        <label htmlFor="ContinueTest">Continue Test</label>
                        <p className="help-block">A session can be continued after being closed.</p>
                        <Checkbox name="ContinueTest" id="ContinueTest" field="ContinueTest" />&nbsp;
                    </div>
                </div>
            </div>
            <div className="modal-footer">
                {!isCreate ? <button className="btn btn-primary btn-outline" onClick={onClone} data-automationid="SessionConfiguration_Clone">Clone</button> : null}
                <button className="btn btn-primary" onClick={onSubmit} data-automationid="SessionConfiguration_Save">Save</button>
            </div>
        </>
    );
}
